<template>
  <transition
    name="modal"
    enter-class="modal--enter"
    enter-active-class="modal--enter-active"
    leave-class="modal--leave"
    leave-active-class="modal--leave-active"
  >
    <div v-if="active" class="modal" @click.self.stop="onClickaway" @keyup.esc="onClose('esc')">
      <div v-on-clickaway="onClickaway" class="modal__dialog">
        <EButton
          v-if="closeButton"
          icon="close"
          class="modal__close"
          data-test-id="close"
          variant="bare"
          @click.stop="onClose('button')"
        />
        <div class="modal__content" @click.stop>
          <slot />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

import { addScrollLock, removeScrollLock } from '~~/common/js/client/screen';
import { focusFocusableChild } from '~~/common/js/client/keyboard-utils';
import EButton from '~~/common/vue/components/button';

export default {
  name: 'EModal',
  components: {
    EButton,
  },
  mixins: [clickaway],
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    closeButton: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    active(value) {
      if (value) {
        // When the modal is shown, we want a fixed body
        // so the body won't scroll
        addScrollLock('modal__no-scroll');
        // defer to next event loop cycle, $nextTick doesn't do the trick here
        setTimeout(() => focusFocusableChild(this.$el), 0);
        this.$emit('modal-display');
      } else {
        // When the modal is hidden,
        // we want to remain at the top of the scroll position
        removeScrollLock('modal__no-scroll');
      }
    },
  },
  beforeDestroy() {
    // ensure the modal does not prevent page scrolling once it's destroyed
    removeScrollLock('modal__no-scroll');
  },
  methods: {
    onClose(flag) {
      this.$emit('close', flag);
    },
    onClickaway() {
      this.onClose('clickaway');
    },
  },
};
</script>

<style lang="scss" scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 1;
  z-index: $z-index-overlay;
  background: var(--color-overlay-primary);
  transition: opacity $timing-s ease;
}

.modal__dialog {
  @include elevation(1);

  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: auto;
  padding: $space-m;
  margin: 0 $space-1s;
  background: var(--color-background-primary);
  border-radius: $border-radius-l;

  @include mobile-l {
    width: 480px;
    height: auto;
    padding: $space-3l;
    margin: initial;
  }
}

.modal__content {
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;

  @include mobile-l {
    height: 100%;
  }
}

.modal__close {
  position: absolute;
  top: $space-m;
  right: $space-m;
}

.modal--enter,
.modal--leave-active {
  opacity: 0;
}
</style>
<style lang="scss">
.modal__no-scroll {
  overflow: hidden;
  height: 100vh;
  width: 100vw;
}
</style>
